//Libraries
import jwt_decode from 'jwt-decode';
import { toast } from 'react-toastify';
import { history } from 'utils/history';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

import { BASENAME_URL_LOGIN } from 'constants/settings';
// Constants
import { AVAILABLE_COMPANIES, AVAILABLE_LANGUAGES, INITIAL_USER, USERS_TYPE_ID } from 'constants/user';

// APIs
import { apiAuthServices } from 'services/api/auth';
import { apiUserServices } from 'services/api/user';
import { goToAuthADFS } from 'services/user';
// Services
import { intlMessages, isDefined } from 'services/util/auxiliaryUtils';

import type { TUserDetails } from 'types/api/user';
import type { TUserStore } from 'types/store/user';
import type { TLocale, TUser } from 'types/user';

export const useUserStore = create<TUserStore>()(
    persist(
        devtools((set, get) => ({
            user: INITIAL_USER as TUser,
            userData: null,
            loadings: {
                isRequestingUserData: false,
            },
            actions: {
                setUser: (user) => set(() => ({ user })),

                setIsUserAuthenticated: (isUserAuthenticated: boolean) =>
                    set(() => ({ user: { ...get().user, auth: { ...get().user.auth, isUserAuthenticated } } })),

                handleLoginSuccess: (token: string) => {
                    localStorage.setItem('userToken', token);
                    const jwtDecodeToken = jwt_decode(token) as { user: TUser; sessionIndex?: string; nameID?: string };
                    localStorage.setItem('uadfs', isDefined(jwtDecodeToken?.sessionIndex) ? 'true' : 'false');
                    const lngObj =
                        AVAILABLE_LANGUAGES.find((obj) => obj.locale === jwtDecodeToken.user.locale.locale) ?? AVAILABLE_LANGUAGES[0];
                    const companyName = AVAILABLE_COMPANIES.find((company) => company.id === jwtDecodeToken.user.company_id)?.name;

                    localStorage.setItem('LOCALE_CODE', lngObj.locale);

                    // Update user state
                    set({
                        user: {
                            ...jwtDecodeToken.user,
                            companyName: companyName || '',
                            locale: lngObj,
                            auth: {
                                isRequesting: false,
                                isUserAuthenticated: true,
                                showPassword: false,
                            },
                        } as TUser,
                    });
                },

                handleLoginFailure: () => {
                    set({
                        user: INITIAL_USER as TUser,
                    });
                },

                switchLanguage: (locale: TLocale) => {
                    const { locale: localeString } = locale;
                    localStorage.setItem('LOCALE_CODE', localeString);
                    set((state) => ({
                        user: {
                            ...state.user,
                            locale: locale,
                        },
                    }));
                },

                logout: () => {
                    localStorage.removeItem('userToken');
                    set({
                        user: INITIAL_USER as TUser,
                    });
                },

                logoutADFS: async () => {
                    const token = localStorage.getItem('userToken');
                    // The session ends here, whatever the API answers.
                    localStorage.removeItem('userToken');
                    if (!token) {
                        history.push(BASENAME_URL_LOGIN);
                        return;
                    }

                    try {
                        const { nameID, sessionIndex } = jwt_decode(token) as { nameID?: string; sessionIndex?: string };
                        goToAuthADFS(await apiAuthServices.logoutAdfs(token, nameID, sessionIndex));
                    } catch {
                        // A failed request was reported by the service; without the identity provider's page, the way out is the login page.
                        history.push(BASENAME_URL_LOGIN);
                    }
                },

                fetchUserData: async () => {
                    const { user } = get();
                    if (!user.id) return;

                    set({ loadings: { isRequestingUserData: true } });

                    try {
                        set({ userData: await apiUserServices.getUserDetails(user.id) });
                    } catch {
                        // Already reported by the service.
                        toast.error(intlMessages('label.error'));
                    } finally {
                        set({ loadings: { isRequestingUserData: false } });
                    }
                },

                updateUserData: async (payload: TUserDetails) => {
                    const { user } = get();
                    if (!user.id) return false;

                    set({ loadings: { isRequestingUserData: true } });

                    try {
                        const token = await apiUserServices.updateUserDetails(user.id, payload);
                        if (token) localStorage.setItem('userToken', token);
                        // The profile page edits only the name and email.
                        set((state) => ({
                            user: { ...state.user, name: payload.name, email: payload.email },
                        }));
                        return true;
                    } catch {
                        // Already reported by the service.
                        toast.error(intlMessages('label.error'));
                        return false;
                    } finally {
                        set({ loadings: { isRequestingUserData: false } });
                    }
                },

                setUserData: (userData: TUserDetails) => {
                    set({ userData });
                },
            },
        })),
        {
            name: 'user',
            // Only the signed-in user is kept between visits.
            partialize: (state) => ({ user: state.user }),
        }
    )
);

// Computed getter for isUserManager
export const getIsUserManager = () => {
    const { user } = useUserStore.getState();
    return user.user_type_id === USERS_TYPE_ID.MANAGER;
};
